import React, { useCallback, useState } from 'react';
import type { Fund, Lot, Portfolio } from 'venn-api';
import { useHasFF } from 'venn-utils';
import { AlertNote, Button, Icon } from 'venn-ui-kit';
import type { SearchMenuItem } from '../search-menu';
import { MultiSelectSearch } from '../search-menu';
import { AllocationSearchContainerRedesigned } from './allocation-search';
import { ModalFooter } from '../modal';
import {
  AddInvestmentCommonHeader,
  AddInvestmentCommonModal,
  TAX_MODAL_WIDTH,
} from '../modals/add-investment-common-modal';
import { useAllocationPanelSearchSelection } from './hooks/useAllocationPanelSearchSelection';
import { TaxInfoStep, createDefaultLot, buildTaxAllocations, buildTaxLots } from './allocation-search/tax-info';
import type { LotInput } from './allocation-search/tax-info';

export interface FundSelectionResult {
  needsManualStrategyAssignment?: boolean;
}

type FlowStep = 'SEARCH' | 'TAX_INFO';

interface AllocationPanelSearchRedesignedProps {
  onCancel: () => void;
  onMultiSelectFund: (items: SearchMenuItem[], allocations?: Map<string, number>, lots?: Map<string, Lot[]>) => void;
  onSingleSelectFund: (
    item: SearchMenuItem,
    constituentId?: number,
  ) => Promise<FundSelectionResult | void> | FundSelectionResult | void;
  onStrategyAssignment?: (fund: Fund, strategyId: string, constituentId?: number) => void;
  onAddTaxInformation: (investmentId: string, lots: LotInput[]) => void;
  strategy: Portfolio;
  nodeRef: React.RefObject<HTMLDivElement>;
  hasTaxInformation?: boolean;
}

export const AllocationPanelSearchRedesigned: React.FC<AllocationPanelSearchRedesignedProps> = ({
  onCancel,
  onMultiSelectFund,
  onSingleSelectFund,
  onStrategyAssignment,
  onAddTaxInformation,
  strategy,
  nodeRef,
  hasTaxInformation = false,
}) => {
  const hasRCMPortfolioAllocatorStrategyEnforcement = useHasFF('rcm_portfolio_strategy_enforcement_ff');

  const { setSelectedItems, filteredSelectedItems, onMultiSelectSubmit, onAddSelection } =
    useAllocationPanelSearchSelection(onMultiSelectFund);

  const [step, setStep] = useState<FlowStep>('SEARCH');
  const [currentInvestmentIndex, setCurrentInvestmentIndex] = useState(0);
  const [selectedForTax, setSelectedForTax] = useState<SearchMenuItem[]>([]);
  const [allLots, setAllLots] = useState<LotInput[][]>([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const handleCurrentLotsChange = useCallback(
    (lots: LotInput[]) => {
      setAllLots((prev) => {
        const updated = [...prev];
        updated[currentInvestmentIndex] = lots;
        return updated;
      });
    },
    [currentInvestmentIndex],
  );

  const boundingRect = nodeRef.current?.getBoundingClientRect();

  if (hasRCMPortfolioAllocatorStrategyEnforcement) {
    return (
      <AllocationSearchContainerRedesigned
        onClose={onCancel}
        onSingleSelectFund={onSingleSelectFund}
        onStrategyAssignment={onStrategyAssignment}
        onAddTaxInformation={onAddTaxInformation}
        strategy={strategy}
        hasTaxInformation={hasTaxInformation}
      />
    );
  }

  const excludedItems = (strategy.children || [])
    .filter((child) => child.fund)
    .map(({ fund }) => ({
      id: fund!.id,
      type: 'FUND' as const,
    }));

  const handleInputTaxInfo = () => {
    const items = [...filteredSelectedItems];
    setSelectedForTax(items);
    setAllLots(items.map(() => [createDefaultLot()]));
    setCurrentInvestmentIndex(0);
    setStep('TAX_INFO');
  };

  const handleTaxBack = () => {
    if (currentInvestmentIndex > 0) {
      setCurrentInvestmentIndex(currentInvestmentIndex - 1);
    } else {
      setStep('SEARCH');
    }
  };

  const handleTaxNext = () => {
    setCurrentInvestmentIndex(currentInvestmentIndex + 1);
  };

  const handleTaxAddSelection = () => {
    const allocations = buildTaxAllocations(selectedForTax, allLots);
    const lots = buildTaxLots(selectedForTax, allLots);
    onMultiSelectSubmit(selectedForTax, allocations, lots);
  };

  const currentInvestment = selectedForTax[currentInvestmentIndex];
  const totalInvestments = selectedForTax.length;
  const isMultiInvestment = totalInvestments > 1;
  const isLastInvestment = currentInvestmentIndex === totalInvestments - 1;

  const currentLots = allLots[currentInvestmentIndex] ?? [createDefaultLot()];
  const hasAtLeastOneLotFilled = currentLots.some(
    (lot) => lot.marketValue !== 0 || lot.costBasis !== 0 || lot.quantity !== 0,
  );

  if (step === 'TAX_INFO' && currentInvestment) {
    const investmentName = currentInvestment.label ?? currentInvestment.value?.fund?.name ?? '';
    const taxTitle = (
      <>
        <strong>Add tax information to: </strong>
        {investmentName}
      </>
    );

    const showAlert = !isMultiInvestment || isLastInvestment;

    return (
      <AddInvestmentCommonModal onClose={onCancel} size={TAX_MODAL_WIDTH}>
        <AddInvestmentCommonHeader title={taxTitle} onClose={onCancel} />
        <TaxInfoStep lots={currentLots} onLotsChange={handleCurrentLotsChange} setSaveDisabled={setIsSaveDisabled} />
        {showAlert && (
          <AlertNote size="small" className="justify-end">
            Lots added to this portfolio apply wherever this portfolio is used.
          </AlertNote>
        )}
        <ModalFooter
          className={FOOTER_CLASS}
          inverted
          simplifiedButtonStyles
          leftChildren={
            <Button refreshedStyling noMargin icon="chevron-left" onClick={handleTaxBack}>
              Back
            </Button>
          }
          rightChildren={
            isMultiInvestment ? (
              <span className="text-[14px] text-venn-black mr-[8px]">
                Investment {currentInvestmentIndex + 1}/{totalInvestments}
              </span>
            ) : undefined
          }
          primaryLabel={
            isLastInvestment || !isMultiInvestment ? (
              <span>Add Selection</span>
            ) : (
              <span className="flex items-center gap-[4px]">
                Next
                <Icon type="chevron-right" />
              </span>
            )
          }
          primaryDisabled={!hasAtLeastOneLotFilled || isSaveDisabled}
          onPrimaryClick={isLastInvestment || !isMultiInvestment ? handleTaxAddSelection : handleTaxNext}
        />
      </AddInvestmentCommonModal>
    );
  }

  const searchTitle = (
    <>
      <strong>Add investment to: </strong>
      {strategy.name}
    </>
  );

  const hasSelections = filteredSelectedItems.length > 0;

  return (
    <AddInvestmentCommonModal onClose={onCancel}>
      <AddInvestmentCommonHeader title={searchTitle} onClose={onCancel} />
      <div className="style-override allocation-panel-multi-select-search flex flex-1 flex-col overflow-hidden py-0 px-4">
        <MultiSelectSearch
          shouldLimitSize={false}
          autofocus
          investmentsOnly
          boundingRect={boundingRect}
          onSelected={onMultiSelectSubmit}
          onSelectionsChange={setSelectedItems}
          excludedItems={excludedItems}
          location="allocationPanel"
          privateAssetSearchMode="PUBLIC_ONLY"
          forceMenuIsOpen
          footer={() => null}
          refreshedStyling
          displayResultsAsBlock
          customPlaceholder="Search for any investment"
          forceShowSearchIcon
          maxResults={20}
        />
      </div>
      <ModalFooter
        className={FOOTER_CLASS}
        inverted
        simplifiedButtonStyles
        primaryLabel={hasTaxInformation ? 'Input tax info' : 'Add Selection'}
        primaryDisabled={!hasSelections}
        onPrimaryClick={hasTaxInformation ? handleInputTaxInfo : onAddSelection}
        secondaryLabel={hasTaxInformation ? 'Skip tax info and add selection' : undefined}
        secondaryDisabled={hasTaxInformation ? !hasSelections : undefined}
        onSecondaryClick={hasTaxInformation ? onAddSelection : undefined}
      />
    </AddInvestmentCommonModal>
  );
};

const FOOTER_CLASS = 'mt-0 py-[12px] px-[16px] h-auto border-t-venn-grey';
